// Import Modules
import colourCorrection from './colour-matrix';
import Rectangle from './geometry';
import { X, Y, WIDTH, HEIGHT } from './iu-constants';
import CropCords from './thumbnail-crop-coords';
import { hslToHex } from '../font-colour-converter';

const Morandi = {
  DEFAULT_RATIO: 6.0 / 4.0,
  largestAspectRectangle(ratio, width, height) {
    let bh;
    let bw;
    let r_ratio;
    let x;
    let y;
    r_ratio = width / height;
    if (r_ratio < ratio) {
      bw = width;
      bh = width / ratio;
      y = (height - bh) / 2;
      return {
        x: 0,
        y,
        width: bw,
        height: bh,
      };
    }
    bh = height;
    bw = height * ratio;
    x = (width - bw) / 2;
    return {
      x,
      y: 0,
      width: bw,
      height: bh,
    };
  },
  roundedRectangle(cr, x1, y1, x2, y2, x_radius, y_radius) {
    let height;
    let width;
    let xr1;
    let xr2;
    let yr1;
    let yr2;
    if (x_radius == null) {
      x_radius = 4;
    }
    if (y_radius == null) {
      y_radius = null;
    }
    width = x2 - x1;
    height = y2 - y1;
    if (y_radius == null) {
      y_radius = x_radius;
    }
    if (x_radius * 2 > width) {
      x_radius = width / 2;
    }
    if (y_radius * 2 > height) {
      y_radius = height / 2;
    }
    xr1 = x_radius;
    xr2 = x_radius / 2.0;
    yr1 = y_radius;
    yr2 = y_radius / 2.0;
    cr.mt(x1 + xr1, y1);
    cr.lt(x2 - xr1, y1);
    cr.bt(x2 - xr2, y1, x2, y1 + yr2, x2, y1 + yr1);
    cr.lt(x2, y2 - yr1);
    cr.bt(x2, y2 - yr2, x2 - xr2, y2, x2 - xr1, y2);
    cr.lt(x1 + xr1, y2);
    cr.bt(x1 + xr2, y2, x1, y2 - yr2, x1, y2 - yr1);
    cr.lt(x1, y1 + yr1);
    cr.bt(x1, y1 + yr2, x1 + xr2, y1, x1 + xr1, y1);
    return cr.cp;
  },
  cloneAndCropCanvasAtScale(source, width, height, ratio) {
    let cr;
    let img;
    let largest;
    let scale;
    let scale1;
    let scale2;
    largest = Morandi.largestAspectRectangle(ratio, source.width, source.height);
    scale1 = height / largest.height;
    scale2 = width / largest.width;
    scale = scale1;
    if (scale2 < scale1) {
      scale = scale2;
    }
    img = document.createElement('canvas');
    cr = img.getContext('2d');
    img.width = largest.width * scale;
    img.height = largest.height * scale;
    cr.drawImage(source, largest.x, largest.y, largest.width, largest.height, 0, 0, img.width, img.height);
    return img;
  },
  cloneCanvas(canvas) {
    const newCanvas = document.createElement('canvas');
    newCanvas.width = canvas.width;
    newCanvas.height = canvas.height;
    const ctx = newCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0);

    return newCanvas;
  },
  cloneCanvasAtAngle(canvas, angle) {
    const newCanvas = document.createElement('canvas');

    if (angle === 90 || angle === 270) {
      newCanvas.width = canvas.height;
      newCanvas.height = canvas.width;
    } else {
      newCanvas.width = canvas.width;
      newCanvas.height = canvas.height;
    }

    const ctx = newCanvas.getContext('2d');
    ctx.translate(newCanvas.width / 2, newCanvas.height / 2);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.translate(canvas.width / -2, canvas.height / -2);
    ctx.drawImage(canvas, 0, 0);
    return newCanvas;
  },
};

function validNumber(val, min, max) {
  if (typeof val !== 'number') {
    return false;
  }
  if (isNaN(val)) {
    return false;
  }
  if (val < min) {
    return false;
  }
  if (val > max) {
    return false;
  }
  return true;
}

function getDefaultCropRatio(ratio, width, height) {
  const r1 = fitLargestRectangle(ratio, width, height);
  const r2 = fitLargestRectangle(1 / ratio, width, height);

  if ((r1[WIDTH] * r1[HEIGHT]) > (r2[WIDTH] * r2[HEIGHT])) {
    return ratio;
  }
  return 1 / ratio;
}
function getDefaultCrop(ratio, width, height) {
  const r1 = fitLargestRectangle(ratio, width, height);
  const r2 = fitLargestRectangle(1 / ratio, width, height);
  let r;

  if ((r1[WIDTH] * r1[HEIGHT]) > (r2[WIDTH] * r2[HEIGHT])) {
    r = r1;
  } else {
    r = r2;
  }
  if (r[X] < 0) {
    r[X] = 0;
  }
  if (r[Y] < 0) {
    r[Y] = 0;
  }
  if (r[WIDTH] + r[X] >= width) {
    r[WIDTH] = Math.floor(width - r[X] - 1);
  }
  if (r[HEIGHT] + r[Y] >= height) {
    r[HEIGHT] = Math.floor(height - r[Y] - 1);
  }

  return r;
}

function getFixedCrop(ratio, width, height) {
  return fitLargestRectangle(ratio, width, height);
}

function scaleRectAroundOrigin(origin, crop, scale) {
  let i;
  let tmp;
  let val;
  let _len;

  tmp = [crop[X] - origin.x, crop[Y] - origin.y, crop[X] + crop[WIDTH] - origin.x, crop[Y] + crop[HEIGHT] - origin.y];

  for (i = 0, _len = tmp.length; i < _len; i++) {
    val = tmp[i];
    tmp[i] = val * scale;
  }

  return [tmp[X] + origin.x, tmp[Y] + origin.y, tmp[WIDTH] - tmp[X], tmp[HEIGHT] - tmp[Y]];
}

// baseHSL is an object in format { h: 52, s: 77, l: [74, 56, 68, 47, 73, 45, 62] }
// h is hue, s is saturation l is array of lightness values to apply across the gradient and a is the alpha
function replaceImageFillWithGradient(image, x, y, baseHSL, canvas, context) {
  // resize canvas as this can be used for multiple text slots and they can be resized
  canvas.width = image.width;
  canvas.height = image.height;

  if (baseHSL.l.length === 1) {
    console.warn(
      'lightness array does not contain enough values, please add more, duplicating value as a fallback',
    );
    baseHSL.l.push(baseHSL.l[0]);
  }

  if (
    !baseHSL.a ||
    typeof baseHSL.a !== 'number' ||
    baseHSL.a < 0.0 ||
    baseHSL.a > 1.0
  ) {
    console.warn(
      'baseHSL.a alpha value is not present, not a number or is out of range (0.0 to 1.0) defaulting to 1.0: ',
      baseHSL.a,
    );
    baseHSL.a = 1.0;
  }

  // composite fill gradient over whole context
  context.globalCompositeOperation = 'copy';
  const linearGradient = context.createLinearGradient(0, 0, x * 3, y * 2);
  for (let i = 0; i < baseHSL.l.length; i++) {
    linearGradient.addColorStop(
      (1 / (baseHSL.l.length - 1)) * i,
      hslToHex(baseHSL.h, baseHSL.s, baseHSL.l[i]),
    );
  }
  context.fillStyle = linearGradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // composite in transparency from image
  context.globalCompositeOperation = 'destination-in';
  context.globalAlpha = baseHSL.a;
  context.drawImage(image, 0, 0);

  return canvas;
}

function replaceImageFillWithWhiteAndThresholdAlpha(image, canvas, context) {
  if (canvas.width === 0 || canvas.height === 0) return;

  try {
    // resize canvas to match image size as this can be used for multiple text slots
    canvas.width = image.width;
    canvas.height = image.height;

    context.drawImage(image, 0, 0);
    context.globalCompositeOperation = 'source-in';

    // IMAGE DATA START
    const imgData = context.getImageData(0, 0, canvas.width, canvas.height);

    // TODO Test if this is quicker vs draw White to canvas
    // Loop through pixel data, set rgb to white and apply threshold to alpha
    const width = imgData.width;
    const height = imgData.height;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelNumber = y * width * 4 + x * 4;
        imgData.data[pixelNumber] = 255;
        imgData.data[pixelNumber + 1] = 255;
        imgData.data[pixelNumber + 2] = 255;
        imgData.data[pixelNumber + 3] >= 200 ? 255 : 0;
      }
    }
    context.putImageData(imgData, 0, 0);

    return canvas;
  } catch (error) {
    console.log(error);
  }
}

const CanvasFilters = {
  colourFX(image, filter, proportion) {
    var a, b, bm, ctx, data, g, gm, i, img, r, rm, s, v, ct, br, x, y, gammaCorrection;
    if (proportion === null) proportion = 1.0;
    if (proportion === 0) return image;
    if (filter === 'colour') return image;
    if (!image || !image.getContext) {
      console.log('image is not suitable for drawing on?');
      return image;
    }
    ctx = image.getContext('2d');

    switch (filter) {
      case 'greyscale':
        rm = gm = bm = 0;
        break;
      case 'sepia':
        rm = 25;
        gm = 5;
        bm = -25;
        break;
      case 'bluetone':
        rm = -10;
        gm = 5;
        bm = 25;
        break;
      case 'clarendon':
        ct = 30; // contrast
        br = -10; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'juno':
        ct = 40; // contrast
        br = 0; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'ludwig':
        ct = 20; // contrast
        br = -20; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'lark':
        ct = 0; // contrast
        br = 20; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'gingham':
        ct = 0; // contrast
        br = 5; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'lofi':
        ct = 50; // contrast
        br = -50; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'toaster':
        ct = 50; // contrast
        br = -40; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      case 'aden':
        ct = 10; // contrast
        br = -10; // brightness
        this.filterLayer(image, filter, ct, br);
        return image;
      default:
      case 'gamma':
        // ignore
        gammaCorrection = proportion;
        break;
        return;
    }
    img = ctx.getImageData(0, 0, image.width, image.height);
    if (filter === 'gamma') {
      if (gammaCorrection === 1.0) {
        return image;
      }
      var table = _gammaTable(proportion);
    }
    i = 0;
    data = img.data;
    y = 0;
    while (y < img.height) {
      x = 0;
      if (i + 4 >= img.length) {
        break;
      }
      while (x < img.width) {
        if (i + 4 >= img.length) {
          break;
        }
        s = i;
        r = data[i++];
        g = data[i++];
        b = data[i++];
        a = data[i++];
        if (filter === 'gamma') {
          data[s++] = table[r];
          data[s++] = table[g];
          data[s++] = table[b];
        } else {
          v = 0.5 * r + 0.3 * b + 0.2 * r;
          data[s++] = Math.clip(Math.clip(v + rm, 0, 255) * proportion + (1 - proportion) * r, 0, 255);
          data[s++] = Math.clip(Math.clip(v + gm, 0, 255) * proportion + (1 - proportion) * g, 0, 255);
          data[s++] = Math.clip(Math.clip(v + bm, 0, 255) * proportion + (1 - proportion) * b, 0, 255);
        }
        if (s + 4 >= img.length) {
          break;
        }
        x++;
      }
      y++;
    }
    ctx.putImageData(img, 0, 0);
    return image;
  },
  filterLayer(image, filter, ct, br) {
    var ctx = image.getContext('2d');
    ctx.drawImage(image, 0, 0);
    var overlay = this.overlayFX(image, filter);
    var screen = this.blend(ctx, overlay, image.width, image.height, function (bottomPixel, topPixel) {
      return 255 - ((255 - topPixel) * (255 - bottomPixel)) / 255;
    });

    var colourCorrected = colourCorrection.colourMatrix(screen, { contrast: ct, brightness: br });

    ctx.putImageData(colourCorrected, 0, 0);

    return image;
  },
  overlayFX(image, filter) {
    const texture = document.createElement('canvas');
    const ctx = texture.getContext('2d');
    let gradient;

    texture.width = image.width;
    texture.height = image.height;

    switch (filter) {
      case 'toaster':
        gradient = ctx.createRadialGradient(
          texture.width / 2,
          texture.height / 2,
          0,
          texture.width / 2,
          texture.height / 2,
          texture.width * 0.7
        );
        gradient.addColorStop(0, '#072A46');
        gradient.addColorStop(1, '#3b003b');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'clarendon':
        gradient = '(127, 197, 237, 0.3)';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'aden':
        gradient = ctx.createLinearGradient(0, 0, texture.width, 0);
        gradient.addColorStop(0, '#290609');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'lark':
        gradient = '(25, 50, 50, 0.7)';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'lofi':
        gradient = ctx.createRadialGradient(
          texture.width / 2,
          texture.height / 2,
          0,
          texture.width / 2,
          texture.height / 2,
          texture.width * 0.6
        );
        gradient.addColorStop(0.7, 'rgb(55,55,55)');
        gradient.addColorStop(1, 'rgb(0,0,0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'gingham':
        gradient = ctx.createLinearGradient(0, 0, texture.width, 0);
        gradient.addColorStop(0, '#290609');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        ctx.filter = 'hue-rotate(350)';
        break;
      case 'juno':
        gradient = 'rgb(0,0,0)';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      case 'ludwig':
        gradient = 'rgb(50,10,10)';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, texture.width, texture.height);
        break;
      default:
        return ctx;
    }

    return ctx;
  },
  blend(background, foreground, width, height, transform) {
    const bottom = background.getImageData(0, 0, width, height);
    const top = foreground.getImageData(0, 0, width, height);

    for (var i = 0, size = top.data.length; i < size; i += 4) {
      // red
      top.data[i + 0] = transform(bottom.data[i + 0], top.data[i + 0]);
      // green
      top.data[i + 1] = transform(bottom.data[i + 1], top.data[i + 1]);
      // blue
      top.data[i + 2] = transform(bottom.data[i + 2], top.data[i + 2]);
      // the fourth slot is alpha. We don't need that (so skip by 4)
    }

    return top;
  },
  greyscale(image, proportion) {if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'greyscale', proportion);
  },
  sepia(image, proportion) {
    if (proportion === null || proportion === undefined) {
      proportion = 1.0;
    }
    return this.colourFX(image, 'sepia', proportion);
  },
  bluetone(image, proportion) {
    if (proportion === null || proportion === undefined) {
      proportion = 1.0;
    }
    return this.colourFX(image, 'bluetone', proportion);
  },

  clarendon(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'clarendon', proportion);
  },
  juno(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'juno', proportion);
  },
  ludwig(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'ludwig', proportion);
  },
  lark(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'lark', proportion);
  },
  gingham(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'gingham', proportion);
  },
  lofi(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'lofi', proportion);
  },
  toaster(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'toaster', proportion);
  },
  aden(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return this.colourFX(image, 'aden', proportion);
  },
  none(image, proportion) {
    if (proportion === null || proportion === undefined) proportion = 1.0;
    return image;
  },
  gamma(image, proportion) {
    if (proportion === null || proportion === 1.0) {
      return image;
    }
    return this.colourFX(image, 'gamma', proportion);
  },
};

/*
A replacement for context.drawImage
(args are for source and destination).
*/

Object.defineProperty(HTMLCanvasElement.prototype, 'naturalWidth', {
  get() {
    return this.width;
  },
  configurable: true,
});

Object.defineProperty(HTMLCanvasElement.prototype, 'naturalHeight', {
  get() {
    return this.height;
  },
  configurable: true,
});

CanvasRenderingContext2D.prototype.mt = function (x, y) {
  return this.moveTo(x, y);
};

CanvasRenderingContext2D.prototype.lt = function (x, y) {
  return this.lineTo(x, y);
};

CanvasRenderingContext2D.prototype.bt = function (x1, y1, x2, y2, x, y) {
  return this.bezierCurveTo(x1, y1, x2, y2, x, y);
};

CanvasRenderingContext2D.prototype.cp = function () {
  return this.clip();
};

CanvasRenderingContext2D.prototype.roundedRect = function (x1, y1, width, height, x_radius, y_radius) {
  let x2;
  let xr1;
  let xr2;
  let y2;
  let yr1;
  let yr2;
  if (x_radius == null) {
    x_radius = 4;
  }
  if (y_radius == null) {
    y_radius = null;
  }
  x2 = x1 + width;
  y2 = y1 + height;
  if (y_radius == null) {
    y_radius = x_radius;
  }
  if (x_radius * 2 > width) {
    x_radius = width / 2;
  }
  if (y_radius * 2 > height) {
    y_radius = height / 2;
  }
  xr1 = x_radius;
  xr2 = x_radius / 2.0;
  yr1 = y_radius;
  yr2 = y_radius / 2.0;
  this.mt(x1 + xr1, y1);
  this.lt(x2 - xr1, y1);
  this.bt(x2 - xr2, y1, x2, y1 + yr2, x2, y1 + yr1);
  this.lt(x2, y2 - yr1);
  this.bt(x2, y2 - yr2, x2 - xr2, y2, x2 - xr1, y2);
  this.lt(x1 + xr1, y2);
  this.bt(x1 + xr2, y2, x1, y2 - yr2, x1, y2 - yr1);
  this.lt(x1, y1 + yr1);
  this.bt(x1, y1 + yr2, x1 + xr2, y1, x1 + xr1, y1);
  this.cp();
  return this;
};

HTMLCanvasElement.prototype.clone = function () {
  const newCanvas = document.createElement('canvas');
  newCanvas.width = this.width;
  newCanvas.height = this.height;
  const ctx = newCanvas.getContext('2d');
  ctx.drawImage(this, 0, 0);
  return newCanvas;
};

HTMLCanvasElement.prototype.cloneRectangle = function (rect, outputAspectRatio) {
  const cropCords = new CropCords(rect, outputAspectRatio);

  const other = document.createElement('canvas');
  other.width = cropCords.canvasWidth;
  other.height = cropCords.canvasHeight;

  const ctx = other.getContext('2d');

  if (cropCords.isFitCrop) {
    // draw the entire source image at the given co-ords in the canvas
    ctx.drawImage(this, cropCords.x, cropCords.y, cropCords.width, cropCords.height);
  } else {
    // draw a cropped source image across the entire canvas
    ctx.drawImage(this, cropCords.x, cropCords.y, cropCords.width, cropCords.height, 0, 0, other.width, other.height);
  }

  return other;
};

Math.clip = function (number, min, max) {
  return Math.max(min, Math.min(number, max));
};

function _gammaTable(gamma) {
  const table = new Array(256);
  let i = 0;
  for (; i < 256; i++) {
    table[i] = Math.clip(255 * Math.pow(i / 255.0, 1.0 / gamma), 0, 255);
  }
  return table;
}

function fitLargestRectangle(ratio, iWidth, iHeight) {
  return Rectangle.fitLargestRectangle(ratio, iWidth, iHeight).map((item) => Math.floor(item));
}

export {
  Morandi, getDefaultCrop, getDefaultCropRatio, getFixedCrop, validNumber, CanvasFilters, scaleRectAroundOrigin, replaceImageFillWithGradient, replaceImageFillWithWhiteAndThresholdAlpha,
};
